import { useState, useEffect } from "react";
import { useDidUpdate } from "@/hooks/useDidUpdate";
import { todayISO } from "@/lib/utils";
import PageMeta from "@/components/common/PageMeta";
import PageBreadcrumb from "@/components/common/PageBreadCrumb";
import Button from "@/components/ui/button/Button";
import { Modal } from "@/components/ui/modal";
import ConfirmModal from "@/components/ui/modal/ConfirmModal";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
} from "@/components/ui/table";
import Label from "@/components/form/Label";
import Input from "@/components/form/input/InputField";
import { feedLogService } from "@/services/dailyLoggingService";
import { flockService, feedFormulaService } from "@/services/masterDataService";
import { handleApiError } from "@/services/api";
import DateRangeFilter, { getDefaultDateRange } from "@/components/common/DateRangeFilter";
import type { FeedLog, Flock, FeedFormula } from "@/types/models";
import { useIsViewer, useIsOwner } from "@/hooks/useRole";
import { useSortable } from "@/hooks/useSortable";
import SortableHeader from "@/components/ui/table/SortableHeader";
import Pagination from "@/components/ui/pagination/Pagination";

const selectClass =
  "h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs focus:border-brand-500 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90";

export default function FeedDistributionPage() {
  const isViewer = useIsViewer();
  const isOwner = useIsOwner();
  const { sortConfig, requestSort } = useSortable("date", "desc");
  const [logs, setLogs] = useState<FeedLog[]>([]);
  const [flocks, setFlocks] = useState<Flock[]>([]);
  const [formulas, setFormulas] = useState<FeedFormula[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const limit = 50;
  const [editing, setEditing] = useState<FeedLog | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<FeedLog | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Filters
  const [filterFlockId, setFilterFlockId] = useState("");
  const [filterFrom, setFilterFrom] = useState(() => getDefaultDateRange().from);
  const [filterTo, setFilterTo] = useState(() => getDefaultDateRange().to);

  // Form state
  const [flockId, setFlockId] = useState("");
  const [date, setDate] = useState(todayISO());
  const [formulaId, setFormulaId] = useState("");
  const [qtyKg, setQtyKg] = useState("");
  const [notes, setNotes] = useState("");

  const loadLogs = async (fromDate?: string, toDate?: string, newOffset?: number) => {
    setError("");
    try {
      const o = newOffset !== undefined ? newOffset : offset;
      const params: Record<string, string | number> = { limit, offset: o };
      if (filterFlockId) params.flock_id = parseInt(filterFlockId);
      const f = fromDate !== undefined ? fromDate : filterFrom;
      const t = toDate !== undefined ? toDate : filterTo;
      if (f) params.from = f;
      if (t) params.to = t;
      params.sort_by = sortConfig.key;
      params.sort_order = sortConfig.direction;
      const res = await feedLogService.list(params);
      // Response is paginated — the interceptor unwraps .data, so res is the paginated envelope
      if (Array.isArray(res)) {
        setLogs(res as unknown as FeedLog[]);
        setTotal((res as unknown as FeedLog[]).length);
      } else {
        const paginated = res as unknown as { data: FeedLog[]; meta: { total: number } };
        setLogs(paginated.data ?? []);
        setTotal(paginated.meta?.total ?? 0);
      }
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const loadDropdowns = async () => {
    try {
      const [flockData, formulaData] = await Promise.all([
        flockService.list(),
        feedFormulaService.list(),
      ]);
      setFlocks((flockData as Flock[]).filter((f) => f.status === "active"));
      setFormulas((formulaData as FeedFormula[]).filter((f) => f.active));
    } catch {
      // silently fail
    }
  };

  useEffect(() => {
    loadDropdowns();
    loadLogs();
  }, []);

  useEffect(() => {
    setLoading(true);
    loadLogs();
  }, [sortConfig]);

  const openCreate = () => {
    setEditing(null);
    const firstFlock = flocks.length > 0 ? flocks[0] : null;
    setFlockId(firstFlock ? firstFlock.id.toString() : "");
    setDate(todayISO());
    setFormulaId(firstFlock?.default_formula_id ? firstFlock.default_formula_id.toString() : "");
    setQtyKg("");
    setNotes("");
    setError("");
    setModalOpen(true);
  };

  const openEdit = (log: FeedLog) => {
    setEditing(log);
    setFlockId(log.flock_id.toString());
    setDate(new Date(log.date).toISOString().split("T")[0]);
    setFormulaId(log.formula_id ? log.formula_id.toString() : "");
    setQtyKg(log.qty_kg.toString());
    setNotes(log.notes || "");
    setError("");
    setModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError("");

    try {
      const data = {
        flock_id: parseInt(flockId),
        date,
        formula_id: formulaId ? parseInt(formulaId) : undefined,
        qty_kg: parseFloat(qtyKg),
        notes: notes || undefined,
      };
      if (editing) {
        await feedLogService.update(editing.id, data);
      } else {
        await feedLogService.create(data);
      }
      setModalOpen(false);
      setLoading(true);
      await loadLogs();
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    try {
      await feedLogService.delete(deleteConfirm.id);
      setDeleteConfirm(null);
      setLoading(true);
      await loadLogs();
    } catch (err) {
      setError(handleApiError(err));
      setDeleteConfirm(null);
    } finally {
      setDeleting(false);
    }
  };

  const handleDateRangeApply = (from: string, to: string) => {
    setFilterFrom(from);
    setFilterTo(to);
    setOffset(0);
    setLoading(true);
    loadLogs(from, to, 0);
  };

  const handleFilter = () => {
    setOffset(0);
    setLoading(true);
    loadLogs(undefined, undefined, 0);
  };

  // A dropdown applies the moment it changes; there is no separate Filter button.
  useDidUpdate(handleFilter, [filterFlockId]);

  const handlePrev = () => {
    const newOffset = Math.max(0, offset - limit);
    setOffset(newOffset);
    loadLogs(undefined, undefined, newOffset);
  };

  const handleNext = () => {
    const newOffset = offset + limit;
    setOffset(newOffset);
    loadLogs(undefined, undefined, newOffset);
  };

  return (
    <>
      <PageMeta title="Feed Distribution | Poultry Book" description="Daily feed logging" />
      <PageBreadcrumb pageTitle="Feed Distribution" />

      <div className="card">
        <div className="flex items-center justify-between px-5 py-4 sm:px-6">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
            Feed Logs {total > 0 && <span className="text-sm font-normal text-gray-500">({total})</span>}
          </h3>
          {!isViewer && (
            <Button size="sm" onClick={openCreate}>
              Log Feed
            </Button>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 px-5 pb-4 sm:px-6">
          <select
            value={filterFlockId}
            onChange={(e) => setFilterFlockId(e.target.value)}
            className={`${selectClass} max-w-[200px]`}
          >
            <option value="">All Flocks</option>
            {flocks.map((f) => (
              <option key={f.id} value={f.id}>
                {f.batch_name}
              </option>
            ))}
          </select>
          <DateRangeFilter onApply={handleDateRangeApply} />
        </div>

        {error && !modalOpen && (
          <div className="mx-5 mb-4 rounded-lg bg-error-50 p-3 text-sm text-error-600 dark:bg-error-500/15 dark:text-error-500 sm:mx-6">
            {error}
          </div>
        )}

        <div className="overflow-x-auto">
          {loading ? (
            <div className="px-5 py-10 text-center text-gray-500 dark:text-gray-400">
              Loading...
            </div>
          ) : logs.length === 0 && !error ? (
            <div className="px-5 py-10 text-center text-gray-500 dark:text-gray-400">
              No feed logs in the selected period.
            </div>
          ) : logs.length === 0 ? null : (
            <Table>
              <TableHeader>
                <TableRow className="border-t border-gray-200 dark:border-gray-800">
                  <SortableHeader label="Date" sortKey="date" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableHeader label="Flock/Shed" sortKey="flock" sortConfig={sortConfig} onSort={requestSort} />
                  <TableCell isHeader className="px-5 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                    Formula
                  </TableCell>
                  <SortableHeader label="Quantity" sortKey="quantity" sortConfig={sortConfig} onSort={requestSort} />
                  <TableCell isHeader className="px-5 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                    Notes
                  </TableCell>
                  {!isViewer && (
                    <TableCell isHeader className="px-5 py-3 text-right text-sm font-medium text-gray-500 dark:text-gray-400">
                      Actions
                    </TableCell>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map((log) => (
                  <TableRow key={log.id} className="border-t border-gray-100 dark:border-gray-800">
                    <TableCell className="px-5 py-3 text-sm text-gray-800 dark:text-white/90">
                      {new Date(log.date).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="px-5 py-3 text-sm text-gray-600 dark:text-gray-400">
                      {log.flock_name}
                    </TableCell>
                    <TableCell className="px-5 py-3 text-sm text-gray-600 dark:text-gray-400">
                      {log.formula_name || "-"}
                    </TableCell>
                    <TableCell className="px-5 py-3 text-sm text-right font-medium text-gray-800 dark:text-white/90">
                      {log.qty_kg.toLocaleString()}
                    </TableCell>
                    <TableCell className="px-5 py-3 text-sm text-gray-500 dark:text-gray-400">
                      {log.notes || "-"}
                    </TableCell>
                    {!isViewer && (
                      <TableCell className="px-5 py-3 text-right">
                        <button
                          onClick={() => openEdit(log)}
                          className="text-sm text-brand-500 hover:text-brand-600 dark:text-brand-400"
                        >
                          Edit
                        </button>
                        {isOwner && (
                          <button
                            onClick={() => setDeleteConfirm(log)}
                            className="ml-3 text-sm text-error-500 hover:text-error-600 dark:text-error-400"
                          >
                            Delete
                          </button>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        <Pagination total={total} limit={limit} offset={offset} onPrev={handlePrev} onNext={handleNext} />
      </div>

      {/* Create Modal */}
      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} className="max-w-md p-5 sm:p-8 mx-4">
        <h4 className="mb-5 text-lg font-semibold text-gray-800 dark:text-white/90">
          {editing ? "Edit Feed Log" : "Log Feed Distribution"}
        </h4>

        {error && modalOpen && (
          <div className="mb-4 rounded-lg bg-error-50 p-3 text-sm text-error-600 dark:bg-error-500/15 dark:text-error-500">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="date">Date *</Label>
            <Input
              id="date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              required
            />
          </div>

          <div>
            <Label htmlFor="flockId">Flock *</Label>
            <select
              id="flockId"
              value={flockId}
              onChange={(e) => {
                const val = e.target.value;
                setFlockId(val);
                if (val) {
                  const flock = flocks.find((f) => f.id.toString() === val);
                  if (flock?.default_formula_id) {
                    setFormulaId(flock.default_formula_id.toString());
                  } else {
                    setFormulaId("");
                  }
                }
              }}
              required
              className={selectClass}
            >
              <option value="">Select flock</option>
              {flocks.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.batch_name} ({f.shed_code})
                </option>
              ))}
            </select>
          </div>

          <div>
            <Label htmlFor="formulaId">Feed Formula</Label>
            <select
              id="formulaId"
              value={formulaId}
              onChange={(e) => setFormulaId(e.target.value)}
              className={selectClass}
            >
              <option value="">None</option>
              {formulas.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <Label htmlFor="qtyKg">Quantity (kg) *</Label>
            <Input
              id="qtyKg"
              type="number"
              value={qtyKg}
              onChange={(e) => setQtyKg(e.target.value)}
              placeholder="e.g. 1500"
              required
              min={0.001}
              step="0.001"
            />
          </div>

          <div>
            <Label htmlFor="notes">Notes</Label>
            <textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional notes"
              rows={2}
              className="w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-500 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" size="sm" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" size="sm" disabled={saving || !flockId || !qtyKg}>
              {saving ? "Saving..." : editing ? "Update" : "Log Feed"}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={!!deleteConfirm}
        title="Delete Entry"
        message={
          <>
            Are you sure you want to delete the feed log for{" "}
            <span className="font-medium">{deleteConfirm && new Date(deleteConfirm.date).toLocaleDateString()}</span>{" "}
            ({deleteConfirm?.qty_kg.toLocaleString()} kg)? This action cannot be undone.
          </>
        }
        busy={deleting}
        busyLabel="Deleting…"
        onConfirm={handleDelete}
        onClose={() => setDeleteConfirm(null)}
      />
    </>
  );
}
